// ─────────────────────────────────────────────────────────────────────────────
// Deterministic SNF mock datasets that back the non-grievance reports.
//
// One seeded generator produces a coherent, stable world across the 6 pilot
// facilities: residents and everything derived from them (incidents, transfers,
// assessments, packets, meds), plus facility-level census/COVID/quality, and
// system/admin operational data. Deterministic so reports look identical on
// every load and across users.
//
// This mirrors the grievance adapter pattern: when a real feed lands, only the
// reportingAdapter changes — report definitions keep reading the same shapes.
// ─────────────────────────────────────────────────────────────────────────────

import { PILOT_FACILITIES } from "../grievanceModel";

// ── seeded PRNG (mulberry32) ─────────────────────────────────────────────────
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rng = mulberry32(20260617);
const rand = () => rng();
const pick = (arr) => arr[Math.floor(rand() * arr.length)];
const int = (min, max) => min + Math.floor(rand() * (max - min + 1));
const chance = (p) => rand() < p;

const todayMs = new Date("2026-06-17").getTime();
const DAY = 24 * 60 * 60 * 1000;
const isoDaysAgo = (d) => new Date(todayMs - d * DAY).toISOString().slice(0, 10);
const isoDaysAhead = (d) => new Date(todayMs + d * DAY).toISOString().slice(0, 10);

// ── name pools ────────────────────────────────────────────────────────────────
const FIRST = ["Eleanor", "Harold", "Doris", "Walter", "Frances", "Robert", "Mabel", "George", "Yolanda", "Stanley", "Irene", "Clyde", "Lucille", "Otis", "Beatrice", "Theodore", "Pearl", "Nathaniel", "Vernon", "Gladys", "Hugh", "Estelle", "Curtis", "Adelaide", "Marvin", "Hazel", "Leonard", "Vivian", "Raymond", "Edith", "Arthur", "Mildred", "Clarence", "Bernice", "Floyd", "Agnes", "Herman", "Lorraine", "Eugene", "Opal"];
const LAST = ["Whitfield", "Simmons", "Vance", "Nguyen", "Abbott", "Esparza", "Carter", "Lindqvist", "Pierce", "Koch", "Sokolov", "Bannister", "Hartman", "Freeman", "Cho", "Marsh", "Donovan", "Boone", "Pratt", "Ferraro", "Castellano", "Park", "Mbeki", "Ross", "Holloway", "Webb", "Tucker", "Boyd", "Salazar", "Reyes"];
const STAFF = ["Maria Delgado, RN", "Karen Whitfield, NHA", "Derek Olsen, NHA", "Renee Tucker, RN", "Gloria Mendez, NHA", "Howard Quinn, NHA", "Felicia Barnes, NHA", "Angela Pruitt, RN", "Patrick Doyle, LPN", "Sandra Kim, DPT"];

const PAYERS = ["Medicare A", "Medicaid", "Managed Care", "Private Pay", "Medicare Advantage"];
const DIRECTIVES = ["Full Code", "DNR", "DNR/DNI", null];
const RISK = ["Low", "Moderate", "High"];

// ── residents (the spine) ─────────────────────────────────────────────────────
const residents = [];
let rid = 1000;
PILOT_FACILITIES.forEach((f, fi) => {
  // Birch Creek (index 0) weighted heaviest
  const count = fi === 0 ? 14 : int(8, 11);
  for (let i = 0; i < count; i++) {
    const level = chance(0.7) ? "SNF" : pick(["AL", "IL"]);
    const losDays = int(3, 420);
    residents.push({
      id: "R" + (++rid),
      name: `${pick(FIRST)} ${pick(LAST)}`,
      facilityCcn: f.ccn,
      facilityName: f.name,
      levelOfCare: level,
      payer: pick(PAYERS),
      admitDate: isoDaysAgo(losDays),
      losDays,
      advanceDirective: pick(DIRECTIVES),
      medsCount: int(3, 22),
      rehospRisk: pick(RISK),
      partADay: int(0, 2) === 0 ? int(3, 100) : null, // ~1/3 on a Part A stay
      recentlyReadmitted: int(0, 6) === 0,             // ~15% recently readmitted
      latestAssessmentDate: isoDaysAgo(int(2, 120)),
      attendingMd: `Dr. ${pick(LAST)}`,
      room: `${int(1, 4)}${String(int(1, 40)).padStart(2, "0")}`,
    });
  }
});
// Ensure the canon demo resident exists on Birch Creek
residents.push({
  id: "R9001", name: "Eleanor Whitfield", facilityCcn: "505289", facilityName: "Birch Creek",
  levelOfCare: "SNF", payer: "Medicare A", admitDate: isoDaysAgo(96), losDays: 96,
  advanceDirective: "DNR", medsCount: 14, rehospRisk: "Moderate", latestAssessmentDate: isoDaysAgo(12),
  partADay: 96, recentlyReadmitted: false,
  attendingMd: "Dr. Marsh", room: "212",
});

const residentsBy = (ccn) => residents.filter((r) => ccn === "ALL" || r.facilityCcn === ccn);

// ── incidents (falls / wounds / elopement) ──────────────────────────────────
const FALL_LOC = ["Resident room", "Bathroom", "Hallway", "Dining room", "Therapy gym", "Common area"];
const WOUND_STAGE = ["Stage 1", "Stage 2", "Stage 3", "Stage 4", "Unstageable", "DTI"];
// PCC "Type/Nature of Incident" labels. The coarse `type` bucket (used by the
// Falls/Wounds suites) is derived from these the same way the live backend's
// normalizeIncident classifies them, so mock and live data behave identically.
const NATURE_BY_TYPE = {
  fall:      ["Fall", "Fall with injury", "Unwitnessed fall", "Fall from bed"],
  wound:     ["Skin tear", "Pressure injury", "Wound deterioration"],
  elopement: ["Elopement", "Elopement attempt"],
  other:     ["Bruise", "Physical Aggression Received", "Physical Aggression Exhibited", "Medication error", "Choking", "Other"],
};
const incidents = [];
let iid = 5000;
residents.forEach((r) => {
  const n = chance(0.45) ? int(1, 3) : 0;
  for (let i = 0; i < n; i++) {
    const type = pick(["fall", "fall", "wound", "elopement", "other"]);
    const nature = pick(NATURE_BY_TYPE[type]);
    const date = isoDaysAgo(int(1, 175));
    const injury = type === "fall" ? chance(0.4) : type === "wound";
    incidents.push({
      id: "INC" + (++iid),
      facilityCcn: r.facilityCcn,
      facilityName: r.facilityName,
      residentName: r.name,
      residentId: r.id,
      type,
      nature,
      date,
      severity: injury ? pick(["Minor", "Moderate", "Major"]) : "None",
      injury,
      location: type === "fall" ? pick(FALL_LOC) : type === "elopement" ? "Exit / perimeter" : "Resident room",
      woundStage: type === "wound" ? pick(WOUND_STAGE) : null,
      carePlanUpdated: chance(0.78),
      responseMinutes: int(2, 45),
      witnessed: chance(0.5),
      repeat: i > 0,
    });
  }
});

// ── hospital transfers / discharges ─────────────────────────────────────────
const TRANSFER_REASON = ["Respiratory distress", "Chest pain", "Sepsis workup", "Fall with injury", "Altered mental status", "GI bleed", "Behavioral", "Wound infection"];
const transfers = [];
let tid = 7000;
residents.forEach((r) => {
  if (chance(0.28)) {
    const kind = pick(["unplanned", "unplanned", "planned", "AMA"]);
    const date = isoDaysAgo(int(1, 170));
    transfers.push({
      id: "TR" + (++tid),
      facilityCcn: r.facilityCcn,
      facilityName: r.facilityName,
      residentName: r.name,
      residentId: r.id,
      kind,
      date,
      reason: pick(TRANSFER_REASON),
      returned: chance(0.7),
      rehospitalized: kind === "unplanned" && chance(0.35),
      los: int(1, 9),
    });
  }
});

// ── MDS / assessments ────────────────────────────────────────────────────────
const ASSESS_TYPE = ["Admission", "Quarterly", "Annual", "Significant Change", "PPS 5-Day", "Discharge"];
const assessments = [];
let aid = 8000;
residents.forEach((r) => {
  const n = int(1, 3);
  for (let i = 0; i < n; i++) {
    const due = int(-20, 25); // negative = overdue
    const completed = due > 5 ? chance(0.4) : chance(0.85);
    assessments.push({
      id: "MDS" + (++aid),
      facilityCcn: r.facilityCcn,
      facilityName: r.facilityName,
      residentName: r.name,
      type: pick(ASSESS_TYPE),
      dueDate: due >= 0 ? isoDaysAhead(due) : isoDaysAgo(-due),
      completedDate: completed ? isoDaysAgo(int(0, 10)) : null,
      status: completed ? "Completed" : due < 0 ? "Overdue" : "Due",
      assessor: pick(STAFF),
    });
  }
});

// ── admission/intake packets ─────────────────────────────────────────────────
const PACKET_TYPES = ["Admission Packet", "Annual Consent", "Advance Directive", "Financial Agreement"];
const packets = [];
let pid = 9000;
residents.forEach((r) => {
  if (chance(0.6)) {
    const total = int(3, 6);
    const signed = int(0, total);
    packets.push({
      id: "PKT" + (++pid),
      facilityCcn: r.facilityCcn,
      facilityName: r.facilityName,
      residentName: r.name,
      packetType: pick(PACKET_TYPES),
      signersTotal: total,
      signersSigned: signed,
      completionPct: Math.round((signed / total) * 100),
      lastUpdated: isoDaysAgo(int(0, 60)),
    });
  }
});

// ── progress notes (counts per resident) ─────────────────────────────────────
const progressNotes = residents.map((r) => ({
  facilityCcn: r.facilityCcn,
  facilityName: r.facilityName,
  residentName: r.name,
  notes7d: int(0, 18),
  notes30d: int(5, 70),
  oneOnOne30d: int(0, 8),
  lastNote: isoDaysAgo(int(0, 9)),
}));

// ── vitals / observations (weight, BP, O2) — 180-day window ──────────────────
// Mirrors the live PCC observations shape: weight rows carry PCC's computed
// warning string verbatim plus parsed pctChange + weightLossFlag.
const observations = [];
let oid = 11000;
const obs = (r, extra) => ({ id: "OBS" + (++oid), facilityCcn: r.facilityCcn, facilityName: r.facilityName, residentName: r.name, residentId: r.id, pctChange: null, weightLossFlag: false, warnings: [], ...extra });
residents.forEach((r) => {
  const wReadings = int(1, 3);
  for (let i = 0; i < wReadings; i++) {
    const sig = chance(0.18); // ~CMS significant-loss prevalence
    const pctChange = +(sig ? -(int(50, 130) / 10) : int(-40, 30) / 10).toFixed(1); // sig: -5.0..-13.0
    const flag = sig && pctChange <= -5;
    const windowDays = pick([30, 90, 180]);
    observations.push(obs(r, {
      type: "Weight", value: int(110, 220), unit: "lb", date: isoDaysAgo(int(1, 180)),
      pctChange, weightLossFlag: flag,
      warnings: flag ? [`${pctChange.toFixed(1)}% change over ${windowDays} day(s)`] : [],
    }));
  }
  if (chance(0.6)) observations.push(obs(r, { type: "Blood Pressure", value: `${int(110, 150)}/${int(60, 95)}`, unit: "mmHg", date: isoDaysAgo(int(0, 30)) }));
  if (chance(0.5)) observations.push(obs(r, { type: "O2 Saturation", value: int(90, 99), unit: "%", date: isoDaysAgo(int(0, 30)) }));
});
// canon demo resident: a clear flagged loss so the report always has a row
observations.push({ id: "OBS" + (++oid), facilityCcn: "505289", facilityName: "Birch Creek", residentName: "Eleanor Whitfield", residentId: "R9001", type: "Weight", value: 138, unit: "lb", date: isoDaysAgo(14), pctChange: -10.0, weightLossFlag: true, warnings: ["-10.0% change over 30 day(s)"] });

// ── facility-level: census / beds ────────────────────────────────────────────
const census = PILOT_FACILITIES.map((f) => {
  const ccnResidents = residents.filter((r) => r.facilityCcn === f.ccn);
  const capacity = Math.max(ccnResidents.length + int(2, 14), 30);
  const occupied = ccnResidents.length;
  return {
    facilityCcn: f.ccn,
    facilityName: f.name,
    capacity,
    occupied,
    available: capacity - occupied,
    occupancyPct: Math.round((occupied / capacity) * 100),
    snf: ccnResidents.filter((r) => r.levelOfCare === "SNF").length,
    al: ccnResidents.filter((r) => r.levelOfCare === "AL").length,
    il: ccnResidents.filter((r) => r.levelOfCare === "IL").length,
    lastLoad: isoDaysAgo(0) + " 06:" + String(int(10, 59)).padStart(2, "0"),
  };
});

// ── COVID-19 (monthly per facility, last 6 months) ───────────────────────────
const covid = [];
PILOT_FACILITIES.forEach((f) => {
  for (let m = 5; m >= 0; m--) {
    const d = new Date(todayMs);
    d.setMonth(d.getMonth() - m);
    covid.push({
      facilityCcn: f.ccn,
      facilityName: f.name,
      month: d.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
      residentCases: int(0, 6),
      staffCases: int(0, 4),
      tests: int(20, 120),
      residentVaxPct: int(82, 99),
      staffVaxPct: int(70, 96),
    });
  }
});

// ── immunizations (per-resident records — matches live PCC immunizations shape) ─
const VAX = [
  { immunization: "COVID-19 (Moderna)", category: "COVID-19", manufacturer: "Moderna" },
  { immunization: "COVID-19 (Pfizer)", category: "COVID-19", manufacturer: "Pfizer" },
  { immunization: "Influenza (quadrivalent)", category: "Influenza", manufacturer: "Sanofi" },
  { immunization: "Pneumococcal PPSV23", category: "Pneumococcal", manufacturer: "Merck" },
  { immunization: "RSV", category: "RSV", manufacturer: "GSK" },
];
const immunizations = [];
residents.forEach((r) => {
  const n = int(1, 3);
  for (let i = 0; i < n; i++) {
    const v = pick(VAX);
    const administered = chance(0.85);
    immunizations.push({
      facilityCcn: r.facilityCcn, facilityName: r.facilityName, residentName: r.name, residentId: r.id,
      immunization: v.immunization, category: v.category, status: administered ? "Administered" : "Pending",
      administered, date: administered ? isoDaysAgo(int(10, 300)) : null,
      doseNumber: v.category === "COVID-19" ? int(1, 3) : 1, manufacturer: v.manufacturer,
    });
  }
});

// ── documents (per-resident admission/consent metadata — live PCC documents shape)
const DOCS = [
  { title: "Admission Agreement", category: "Admission" },
  { title: "Consent for Treatment", category: "Consent" },
  { title: "Advance Directive / POLST", category: "Advance Directive" },
  { title: "Face Sheet", category: "Face Sheet" },
  { title: "Financial Responsibility", category: "Consent" },
];
const documents = [];
residents.forEach((r) => {
  DOCS.forEach((d) => {
    if (chance(0.7)) {
      const signed = chance(0.72);
      documents.push({
        facilityCcn: r.facilityCcn, facilityName: r.facilityName, residentName: r.name, residentId: r.id,
        title: d.title, documentType: d.title, category: d.category,
        status: signed ? "Signed" : "Pending signature", signed, date: isoDaysAgo(int(1, 90)),
      });
    }
  });
});

// ── quality measures (per facility) ───────────────────────────────────────────
const qualityMeasures = PILOT_FACILITIES.map((f) => ({
  facilityCcn: f.ccn,
  facilityName: f.name,
  fallsWithInjury: +(rand() * 5 + 1).toFixed(1),
  pressureUlcers: +(rand() * 4 + 0.5).toFixed(1),
  antipsychotics: +(rand() * 12 + 6).toFixed(1),
  utis: +(rand() * 3 + 0.5).toFixed(1),
  weightLoss: +(rand() * 6 + 2).toFixed(1),
  catheter: +(rand() * 3 + 0.5).toFixed(1),
  longStayHosp: +(rand() * 18 + 8).toFixed(1),
  stayType: "Long-Stay",
}));
const qualityMeasuresShort = PILOT_FACILITIES.map((f) => ({
  facilityCcn: f.ccn,
  facilityName: f.name,
  improvedFunction: +(rand() * 20 + 55).toFixed(1),
  rehospitalized: +(rand() * 10 + 12).toFixed(1),
  edVisits: +(rand() * 8 + 6).toFixed(1),
  newAntipsychotic: +(rand() * 3 + 1).toFixed(1),
  communityDischarge: +(rand() * 20 + 45).toFixed(1),
  stayType: "Short-Stay",
}));

// ── GG function outcomes (per resident, SNF) ─────────────────────────────────
// Section GG — per-item rows on the real 06→01 performance scale (06 independent
// → 01 dependent), mirroring the live shape: observed admission → current change
// from locked MDS, with in-house residents marked outcome-pending.
const GG_ITEMS = ["Eating", "Toileting hygiene", "Sit to stand", "Chair/bed-to-chair transfer", "Walk 50 feet with two turns", "Walk 150 feet"];
const ggFunction = [];
residents
  .filter((r) => r.levelOfCare === "SNF")
  .forEach((r) => {
    const pending = chance(0.3); // still in-house — admission only
    GG_ITEMS.slice(0, int(3, 5)).forEach((item) => {
      const admit = int(1, 4);
      const current = pending ? admit : Math.min(6, admit + int(0, 3));
      ggFunction.push({
        facilityCcn: r.facilityCcn,
        facilityName: r.facilityName,
        residentName: r.name,
        residentId: r.id,
        questionKey: item,
        domain: item,
        admitScore: admit,
        currentScore: pending ? admit : current,
        goalScore: Math.min(6, admit + int(1, 2)),
        change: pending ? null : current - admit,
        status: pending ? "Outcome pending (in-house)" : "Observed",
      });
    });
  });

// ── ADL flowsheet documentation (near-real-time, per resident) ───────────────
// Mirrors the ADL webhook's normalizeAdl shape: CNA-charted self-performance per
// ADL activity with an MDS Section-G dependence level (0 independent → 4 total).
// A level of 3-4 is F677 exposure; a worsening trend across dates is F676.
const ADL_ACTIVITIES = ["Bed mobility", "Transfer", "Walk in room", "Locomotion on unit", "Dressing", "Eating", "Toilet use", "Personal hygiene", "Bathing"];
const ADL_SELF_PERF = [
  { text: "Independent", level: 0 },
  { text: "Supervision", level: 1 },
  { text: "Limited assistance", level: 2 },
  { text: "Extensive assistance", level: 3 },
  { text: "Total dependence", level: 4 },
];
// ── conditions (resident problem list / diagnoses) ──────────────────────────
// Mirrors the PCC conditions domain (normalizeCondition): one row per coded
// diagnosis with ICD-10, description, clinical category, principal/secondary rank.
const DX_POOL = [
  { code: "I10", description: "Essential (primary) hypertension", category: "Other" },
  { code: "E11.9", description: "Type 2 diabetes mellitus without complications", category: "Diabetes" },
  { code: "N39.0", description: "Urinary tract infection, site not specified", category: "UTI", infection: true },
  { code: "J18.9", description: "Pneumonia, unspecified organism", category: "Respiratory infection", infection: true },
  { code: "A41.9", description: "Sepsis, unspecified organism", category: "Sepsis", infection: true },
  { code: "I69.351", description: "Hemiplegia following cerebral infarction affecting right dominant side", category: "Stroke" },
  { code: "F02.80", description: "Dementia in other diseases without behavioral disturbance", category: "Dementia" },
  { code: "J44.9", description: "Chronic obstructive pulmonary disease, unspecified", category: "COPD" },
  { code: "M25.561", description: "Pain in right knee", category: "Other" },
  { code: "E78.5", description: "Hyperlipidemia, unspecified", category: "Other" },
  { code: "I25.10", description: "Atherosclerotic heart disease of native coronary artery", category: "Other" },
  { code: "L89.153", description: "Pressure ulcer of sacral region, stage 3", category: "Wound" },
  { code: "E43", description: "Unspecified severe protein-calorie malnutrition", category: "Malnutrition" },
  { code: "F32.9", description: "Major depressive disorder, single episode, unspecified", category: "Other" },
  { code: "G20", description: "Parkinson's disease", category: "Other" },
];
const conditions = [];
residents.forEach((r) => {
  const n = int(2, 5);
  const picks = [];
  for (let i = 0; i < n; i++) {
    const d = DX_POOL[int(0, DX_POOL.length - 1)];
    if (picks.find((p) => p.code === d.code)) continue;
    picks.push(d);
  }
  picks.forEach((d, i) => {
    conditions.push({
      facilityCcn: r.facilityCcn, facilityName: r.facilityName,
      residentName: r.name, residentId: r.id,
      code: d.code, description: d.description, category: d.category,
      isInfection: !!d.infection,
      status: "Active",
      rank: i === 0 ? "Principal" : "Secondary",
      onsetDate: isoDaysAgo(int(20, 800)),
      coded: true,
    });
  });
});

// ── episodes of care (stay-level periods, by payer) ─────────────────────────
// Mirrors the PCC episodes domain (normalizeEpisode): one active stay per
// resident, and ~1 in 4 with a prior discharged stay (a readmission signal).
const EPISODE_PAYERS = ["Medicare A", "Managed Care", "Medicaid", "Private", "Medicare A"];
const episodes = [];
residents.forEach((r) => {
  const payer = EPISODE_PAYERS[int(0, EPISODE_PAYERS.length - 1)];
  const admitDays = int(5, 120);
  episodes.push({
    id: `ep-${r.id}-cur`, facilityCcn: r.facilityCcn, facilityName: r.facilityName,
    residentName: r.name, residentId: r.id,
    payerType: payer, isMedicareA: payer === "Medicare A",
    admissionDate: isoDaysAgo(admitDays), dischargeDate: null,
    status: "Active", active: true, lengthOfStay: admitDays,
  });
  if (int(1, 4) === 1) {
    const pAdmit = int(200, 400);
    const pDisc = int(130, 199);
    episodes.push({
      id: `ep-${r.id}-prev`, facilityCcn: r.facilityCcn, facilityName: r.facilityName,
      residentName: r.name, residentId: r.id,
      payerType: "Medicare A", isMedicareA: true,
      admissionDate: isoDaysAgo(pAdmit), dischargeDate: isoDaysAgo(pDisc),
      status: "Discharged", active: false, lengthOfStay: Math.max(1, pAdmit - pDisc),
    });
  }
});

const adl = [];
residents
  .filter((r) => r.levelOfCare === "SNF")
  .forEach((r) => {
    ADL_ACTIVITIES.slice(0, int(4, 7)).forEach((activity) => {
      const sp = ADL_SELF_PERF[int(0, 4)];
      adl.push({
        facilityCcn: r.facilityCcn,
        facilityName: r.facilityName,
        residentName: r.name,
        residentId: r.id,
        activity,
        selfPerformance: sp.text,
        support: pick(["Setup help only", "One-person physical assist", "Two-person physical assist", "No setup or physical help"]),
        dependenceLevel: sp.level,
        needsAssist: sp.level >= 2,
        date: isoDaysAgo(int(0, 20)),
        scored: true,
      });
    });
  });

// ── PDPM HIPPS / case-mix (finalized on accepted MDS) ────────────────────────
// Mirrors the mdsAssessment.accepted webhook's normalizeHipps shape: one row per
// accepted assessment with the 5-char HIPPS and its PT/OT · SLP · Nursing · NTA
// CMG components + the next anticipated ARD (assessment-window close). Some ARDs
// are already in the past so the "closing" logic has both closed + upcoming.
const NURSING_CMG = ["ES3", "ES2", "HDE2", "HBC2", "LDE2", "LBC1", "CBC2", "CA2", "PA1", "PDE1", "BAB1"];
const PTOT_CMG = ["TA", "TB", "TC", "TD", "TE", "TF", "TG", "TI", "TJ", "TK", "TN", "TP"];
const SLP_CMG = ["SA", "SB", "SC", "SD", "SE", "SF", "SG", "SH", "SI", "SJ", "SK", "SL"];
const NTA_CMG = ["NA", "NB", "NC", "ND", "NE", "NF"];
const HIPPS_ASMT_TYPES = ["5-Day PPS Assessment", "IPA (Interim Payment)", "Part A PPS Discharge"];
const lastChar = (code) => code.charAt(code.length - 1);
const hipps = [];
residents
  .filter((r) => r.levelOfCare === "SNF")
  .forEach((r) => {
    const ptot = pick(PTOT_CMG), slp = pick(SLP_CMG), nurs = pick(NURSING_CMG), nta = pick(NTA_CMG);
    // HIPPS = PT/OT char · SLP char · Nursing char · NTA char · AI-code digit.
    const hippsCode = `${lastChar(ptot)}${lastChar(slp)}${nurs.charAt(0)}${lastChar(nta)}1`;
    const nextDays = int(-5, 25); // some windows already closed, some upcoming
    hipps.push({
      facilityCcn: r.facilityCcn,
      facilityName: r.facilityName,
      residentName: r.name,
      residentId: r.id,
      assessmentId: 90000 + int(1, 9999),
      assessmentType: pick(HIPPS_ASMT_TYPES),
      assessmentStatus: chance(0.8) ? "Accepted" : pick(["Completed", "Export Ready"]),
      assessmentRefDate: isoDaysAgo(int(5, 40)),
      nextAssessmentRefDate: nextDays >= 0 ? isoDaysAhead(nextDays) : isoDaysAgo(-nextDays),
      effectiveDate: isoDaysAgo(int(3, 35)),
      hippsCode,
      model: "PDPM",
      ptOtCmg: ptot,
      slpCmg: slp,
      nursingCmg: nurs,
      nursingCmgDescription: pick(["Special Care High", "Special Care Low", "Clinically Complex", "Reduced Physical Function"]),
      ntaCmg: nta,
    });
  });

// ── system / admin operational data ───────────────────────────────────────────
const sysUsers = STAFF.map((s, i) => ({
  name: s.split(",")[0],
  email: s.split(",")[0].toLowerCase().replace(/[^a-z]/g, ".") + "@survaihealth.com",
  role: pick(["Admin", "Clinical", "Reporting", "Read-Only", "Facility Admin"]),
  status: chance(0.85) ? "Active" : "Disabled",
  lastLogin: isoDaysAgo(int(0, 30)) + " " + String(int(7, 19)).padStart(2, "0") + ":" + String(int(0, 59)).padStart(2, "0"),
  facilities: chance(0.4) ? "All" : pick(PILOT_FACILITIES).name,
  mfa: chance(0.7),
}));

const sysJobs = [
  "PointClickCare Census Sync", "MDS Import", "Incident Feed", "Quality Measure Refresh", "COVID Report Pull",
  "Pharmacy MAR Sync", "Grievance Relay", "Nightly Aggregation", "User Provisioning", "Audit Log Export",
].map((name) => {
  const ok = chance(0.82);
  return {
    job: name,
    lastRun: isoDaysAgo(0) + " " + String(int(0, 6)).padStart(2, "0") + ":" + String(int(0, 59)).padStart(2, "0"),
    status: ok ? "Success" : chance(0.5) ? "Warning" : "Failed",
    durationSec: int(4, 480),
    records: int(0, 5200),
  };
});

const subscriptions = PILOT_FACILITIES.map((f) => ({
  facilityCcn: f.ccn,
  facilityName: f.name,
  ehr: pick(["PointClickCare", "MatrixCare", "American HealthTech"]),
  modules: ["Clinical", "Grievance", "Risk", chance(0.5) ? "Quality" : null].filter(Boolean).join(", "),
  seats: int(8, 60),
  renewal: isoDaysAhead(int(20, 300)),
  status: chance(0.9) ? "Active" : "Trial",
}));

const reportUsage = [
  "Grievance Log Dashboard", "Grievance List", "High Risk Falls", "Hospital Transfer Log",
  "Assessments Due", "Available Beds", "Incident List", "Quality Measures", "Rehospitalization Risk",
  "Clinical Report",
].map((name) => ({
  report: name,
  runs30d: int(3, 240),
  uniqueUsers: int(1, 12),
  lastRunBy: sysUsers[int(0, sysUsers.length - 1)].name,
  lastRun: isoDaysAgo(int(0, 14)),
  avgMs: int(120, 3400),
}));

const censusLoadLog = PILOT_FACILITIES.flatMap((f) =>
  [0, 1, 2].map((d) => ({
    facilityCcn: f.ccn,
    facilityName: f.name,
    loadDate: isoDaysAgo(d),
    recordsLoaded: int(28, 120),
    status: chance(0.9) ? "Success" : "Partial",
    durationSec: int(3, 60),
    source: "PointClickCare",
  }))
);

const accountAudit = sysUsers.slice(0, 8).map((u) => ({
  user: u.name,
  action: pick(["Login", "Password Reset", "Role Changed", "Account Created", "Permission Granted", "Login Failed"]),
  date: isoDaysAgo(int(0, 40)) + " " + String(int(0, 23)).padStart(2, "0") + ":" + String(int(0, 59)).padStart(2, "0"),
  ip: `10.0.${int(0, 255)}.${int(1, 254)}`,
  result: chance(0.9) ? "Success" : "Denied",
}));

const accountChanges = sysUsers.slice(0, 7).map((u) => ({
  user: u.name,
  field: pick(["Role", "Facility Access", "Status", "MFA", "Email"]),
  oldValue: pick(["Read-Only", "Disabled", "Off", "Facility A"]),
  newValue: pick(["Reporting", "Active", "On", "All"]),
  changedBy: "system.admin",
  date: isoDaysAgo(int(0, 50)),
}));

const rolePermissions = [
  { role: "Admin", reports: "All", create: "Yes", admin: "Yes", export: "Yes" },
  { role: "Clinical", reports: "Clinical, Risk, Grievance", create: "Yes", admin: "No", export: "Yes" },
  { role: "Reporting", reports: "All (read)", create: "No", admin: "No", export: "Yes" },
  { role: "Facility Admin", reports: "Own facility", create: "Yes", admin: "Partial", export: "Yes" },
  { role: "Read-Only", reports: "Assigned", create: "No", admin: "No", export: "No" },
];

const availableReports = [
  { report: "Grievance Log Dashboard", category: "Grievance", status: "Live", owner: "Compliance" },
  { report: "High Risk Falls", category: "Risk", status: "Live", owner: "Clinical" },
  { report: "Quality Measures", category: "Quality", status: "Live", owner: "Quality" },
  { report: "Hospital Transfer Log", category: "Clinical", status: "Live", owner: "Clinical" },
  { report: "Assessments Due", category: "Clinical", status: "Live", owner: "MDS" },
  { report: "COVID-19 Vaccination Status", category: "COVID-19", status: "Live", owner: "IP" },
  { report: "Report Usage Summary", category: "Admin", status: "Live", owner: "Admin" },
];

// ── domain registry ───────────────────────────────────────────────────────────
export const DATASETS = {
  residents,
  incidents,
  transfers,
  assessments,
  observations,
  packets,
  progressNotes,
  census,
  covid,
  immunizations,
  documents,
  qualityMeasures,
  qualityMeasuresShort,
  ggFunction,
  adl,
  hipps,
  conditions,
  episodes,
  sysUsers,
  sysJobs,
  subscriptions,
  reportUsage,
  censusLoadLog,
  accountAudit,
  accountChanges,
  rolePermissions,
  availableReports,
};

// Datasets that carry a facilityCcn get filtered by scope; the rest are global.
export function getDomainRows(domain, facilityCcn = "ALL") {
  const rows = DATASETS[domain] || [];
  if (facilityCcn === "ALL") return rows.map((r) => ({ ...r }));
  if (rows.length && "facilityCcn" in rows[0]) {
    return rows.filter((r) => r.facilityCcn === facilityCcn).map((r) => ({ ...r }));
  }
  return rows.map((r) => ({ ...r }));
}

export { residentsBy };
